import * as React from "react";
import { Form, useFormikContext } from "formik";
import Stack from "@mui/material/Stack";
import { DropzoneField } from "components/formikFields/DropzoneField/DropzoneField";
import { SelectOption } from "@mui/base";
import { useTranslation } from "react-i18next";
import { SelectOneAutocompleteField } from "components/formikFields/SelectOneAutocompleteField/SelectOneAutocompleteField";
import { SelectManyAutocompleteField } from "components/formikFields/SelectManyAutocompleteField/SelectManyAutocompleteField";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import Button from "@mui/material/Button";
import { LltsLanguage, MachineTranslationSettings, Xl8DubbingVoice, Xl8SubtitleType } from "gen/clients/llts";
import { Box, CircularProgress, Link } from "@mui/material";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import Typography from "@mui/material/Typography";
import { SelectManyField } from "components/formikFields/SelectManyField/SelectManyField";

const SUPPORTED_FILE_TYPES = [
  "video/mp4",
  "video/x-msvideo",
  "video/quicktime",
  "video/x-matroska",
  "video/x-flv",
  "video/x-ms-wmv",
  "video/x-m4v",
  "video/webm",
  "audio/mpeg",
  "audio/wav",
  "audio/aac",
  "audio/flac",
  "audio/ogg",
  "audio/mp4",
  "audio/x-ms-wma",
  "audio/m4a",
  "audio/x-m4a"
];

interface Props {
  mtSettings: MachineTranslationSettings;
  sourceLanguageOptions: SelectOption<string>[];
  targetLanguageOptions: SelectOption<string>[];
  areXl8DubbingVoicesLoading: boolean;
  dubbingVoicesByTargetLanguage: Map<LltsLanguage, Xl8DubbingVoice[]>;
  onSourceLanguageChange: (lltsLanguageId: string | undefined) => void;
  onTargetLanguagesChange: (lltsLanguageIds: string[]) => void;
  onCancel: () => void;
  hideTargetLanguageSelection?: boolean;
  hideSubtitleTypeSelection?: boolean;
  enableVoiceSelection?: boolean;
}

export enum FieldName {
  projectName = "projectName",
  files = "files",
  sourceLanguage = "sourceLanguage",
  targetLanguages = "targetLanguages",
  subtitlesType = "subtitlesType",
  dubbingVoicePrefix = "dubbingVoice"
}

export interface FormValues {
  [FieldName.projectName]: string;
  [FieldName.files]: File[];
  [FieldName.sourceLanguage]: SelectOption<string> | null;
  [FieldName.targetLanguages]: SelectOption<string>[];
  [FieldName.subtitlesType]: Xl8SubtitleType[];
  [key: `${FieldName.dubbingVoicePrefix}#${string}`]: string;
}

const SHORT_SUBTITLE_TYPE_OPTIONS: SelectOption<string>[] = [
  {
    label: "SubRip Subtitle (SRT)",
    value: Xl8SubtitleType.SRT
  },
  {
    label: "Word Document (docx)",
    value: Xl8SubtitleType.DOCX
  },
  {
    label: "JSON",
    value: Xl8SubtitleType.JSON
  },
  {
    label: "Text File (txt)",
    value: Xl8SubtitleType.TXT
  }
];

const ALL_SUBTITLE_TYPE_OPTIONS: SelectOption<string>[] = [
  {
    label: "SubRip Subtitle (SRT)",
    value: Xl8SubtitleType.SRT
  },
  {
    label: "Word Document (docx)",
    value: Xl8SubtitleType.DOCX
  },
  {
    label: "JSON",
    value: Xl8SubtitleType.JSON
  },
  {
    label: "Text File (txt)",
    value: Xl8SubtitleType.TXT
  },
  {
    label: "PAC",
    value: Xl8SubtitleType.PAC
  },
  {
    label: "DFXP",
    value: Xl8SubtitleType.DFXP
  },
  {
    label: "VTT",
    value: Xl8SubtitleType.VTT
  },
  {
    label: "IMSC",
    value: Xl8SubtitleType.IMSC
  },
  {
    label: "ITT",
    value: Xl8SubtitleType.ITT
  },
  {
    label: "SCC",
    value: Xl8SubtitleType.SCC
  },
  {
    label: "SMI",
    value: Xl8SubtitleType.SMI
  },
  {
    label: "TTML",
    value: Xl8SubtitleType.TTML
  },
  {
    label: "STL",
    value: Xl8SubtitleType.STL
  },
  {
    label: "XIF",
    value: Xl8SubtitleType.XIF
  },
  {
    label: "XLSX",
    value: Xl8SubtitleType.XLSX
  }
];

const CreateMediaProjectForm: React.FC<Props> = ({
  mtSettings,
  sourceLanguageOptions,
  targetLanguageOptions,
  areXl8DubbingVoicesLoading,
  dubbingVoicesByTargetLanguage,
  onSourceLanguageChange,
  onTargetLanguagesChange,
  onCancel,
  hideSubtitleTypeSelection,
  hideTargetLanguageSelection,
  enableVoiceSelection
}) => {
  const { t } = useTranslation();
  const { values, isSubmitting } = useFormikContext<FormValues>();
  const [showAllSubtitleTypes, setShowAllSubtitleTypes] = React.useState(false);

  const sourceLanguageId = values[FieldName.sourceLanguage]?.value;
  React.useEffect(() => {
    onSourceLanguageChange(sourceLanguageId);
  }, [onSourceLanguageChange, sourceLanguageId]);

  const targetLanguages = values[FieldName.targetLanguages];
  React.useEffect(() => {
    const targetLanguageIds = targetLanguages.map(l => l.value);
    onTargetLanguagesChange(targetLanguageIds);
  }, [onTargetLanguagesChange, targetLanguages]);

  const dubbingVoiceByVoiceName: Record<string, Xl8DubbingVoice> = React.useMemo(() => {
    return Array.from(dubbingVoicesByTargetLanguage.values())
      .flat()
      .reduce<Record<string, Xl8DubbingVoice>>((acc, voice) => {
        acc[voice.voiceName] = voice;
        return acc;
      }, {});
  }, [dubbingVoicesByTargetLanguage]);

  return (
    <Form noValidate={true} autoComplete="off" autoCorrect="off">
      <Stack spacing={3}>
        <DropzoneField
          name={FieldName.files}
          fileUploadLimits={{
            maxFileCount: mtSettings.maxFilesPerRequest,
            acceptedFiles: SUPPORTED_FILE_TYPES
          }}
          dropzoneText={t("machineTranslation.mediaTranslation.filesDropzone.message")}
          required={true}
          requiredMessage={t("machineTranslation.mediaTranslation.validation.filesRequired")}
        />
        <TextInputField
          name={FieldName.projectName}
          label={t("machineTranslation.mediaTranslation.projectName.label")}
          required={true}
          requiredMessage={t("machineTranslation.mediaTranslation.projectName.required")}
          preventCharacters={[";"]}
          preventCharactersMessage={t("common.validation.semicolonNotAllowed")}
          maxLength={140}
          maxLengthMessage={t("common.validation.projectNameMaxLength")}
        />
        <SelectOneAutocompleteField
          name={FieldName.sourceLanguage}
          label={t("machineTranslation.mediaTranslation.sourceLanguage.label")}
          helperText={t("machineTranslation.mediaTranslation.sourceLanguage.helperText")}
          options={sourceLanguageOptions}
          required={true}
          requiredMessage={t("machineTranslation.mediaTranslation.sourceLanguage.required")}
        />
        {!hideTargetLanguageSelection && (
          <SelectManyAutocompleteField
            name={FieldName.targetLanguages}
            label={t("machineTranslation.mediaTranslation.targetLanguages.label")}
            helperText={t("machineTranslation.mediaTranslation.targetLanguages.helperText")}
            options={targetLanguageOptions}
            required={true}
            requiredMessage={t("machineTranslation.mediaTranslation.targetLanguages.required")}
          />
        )}
        {!hideSubtitleTypeSelection && (
          <SelectManyField
            name={FieldName.subtitlesType}
            label={t("machineTranslation.mediaTranslation.subtitlesType.label")}
            helperText={t("machineTranslation.mediaTranslation.subtitlesType.helperText")}
            options={showAllSubtitleTypes ? ALL_SUBTITLE_TYPE_OPTIONS : SHORT_SUBTITLE_TYPE_OPTIONS}
            required={true}
            requiredMessage={t("machineTranslation.mediaTranslation.subtitlesType.required")}
            variant="standard"
            optionsFooter={
              !showAllSubtitleTypes ? (
                <Box mx={3} mt={1} mb={2}>
                  <Link style={{ cursor: "pointer" }} onClick={() => setShowAllSubtitleTypes(true)}>
                    More options...
                  </Link>
                </Box>
              ) : undefined
            }
          />
        )}
        {enableVoiceSelection && (
          <>
            {areXl8DubbingVoicesLoading ? (
              <CircularProgress />
            ) : (
              <Stack spacing={2}>
                {Array.from(dubbingVoicesByTargetLanguage.entries()).map(([language, voices]) => (
                  <Stack key={language.id} direction="row" spacing={2}>
                    <SelectOneField
                      name={`${FieldName.dubbingVoicePrefix}#${language.id}`}
                      label={t("machineTranslation.mediaTranslation.dubbingVoice.label", {
                        language: language.name
                      })}
                      helperText={t("machineTranslation.mediaTranslation.dubbingVoice.helperText", {
                        language: language.name
                      })}
                      options={voices.map(v => ({ label: v.displayName, value: v.voiceName }))}
                      required={true}
                      requiredMessage={t("machineTranslation.mediaTranslation.dubbingVoice.required")}
                    />
                    {values[`${FieldName.dubbingVoicePrefix}#${language.id}`] &&
                      dubbingVoiceByVoiceName[values[`${FieldName.dubbingVoicePrefix}#${language.id}`]]?.sampleUrl && (
                        <Box key={values[`${FieldName.dubbingVoicePrefix}#${language.id}`]}>
                          <audio
                            controls={true}
                            src={
                              dubbingVoiceByVoiceName[values[`${FieldName.dubbingVoicePrefix}#${language.id}`]]
                                ?.sampleUrl
                            }
                          ></audio>
                        </Box>
                      )}
                  </Stack>
                ))}
              </Stack>
            )}
          </>
        )}
        <Stack direction="row" spacing={1} justifyContent="center" pt={5}>
          <LoadingButton isLoading={isSubmitting} disabled={areXl8DubbingVoicesLoading}>
            {t("machineTranslation.mediaTranslation.submitBtn")}
          </LoadingButton>
          <Button onClick={onCancel}>{t("common.cancel")}</Button>
        </Stack>

        <Box pt={4}>
          <Typography
            variant="body2"
            sx={theme => ({ color: theme.palette.grey["700"] })}
            dangerouslySetInnerHTML={{ __html: t("machineTranslation.mediaTranslation.disclaimer") }}
          />
        </Box>
      </Stack>
    </Form>
  );
};

export { CreateMediaProjectForm };
